import { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useBusinessStages } from '@/hooks/useBusinessStages';

interface BusinessStageSelectorProps {
  brandId: string;
  value: string | null;
  onChange: (stageId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function BusinessStageSelector({
  brandId,
  value,
  onChange,
  placeholder = 'Select a stage...',
  disabled = false,
}: BusinessStageSelectorProps) {
  const [open, setOpen] = useState(false);
  const { businessStages, isLoading } = useBusinessStages(brandId);

  const selectedStage = businessStages.find((s) => s.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className="w-full justify-between"
        >
          {isLoading ? (
            <span className="text-muted-foreground">Loading...</span>
          ) : selectedStage ? (
            <div className="flex items-center gap-2">
              <div
                className="h-3 w-3 rounded-full shrink-0"
                style={{ backgroundColor: selectedStage.color }}
              />
              <span>{selectedStage.name}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 z-[9999]" align="start">
        <Command>
          <CommandInput placeholder="Search stages..." />
          <CommandList className="max-h-[200px] overflow-y-auto">
            <CommandEmpty>No stages found.</CommandEmpty>
            <CommandGroup>
              {/* Clear option */}
              <CommandItem
                value="__clear__"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <X className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">No stage</span>
              </CommandItem>

              {businessStages.map((stage) => (
                <CommandItem
                  key={stage.id}
                  value={stage.name}
                  onSelect={() => {
                    onChange(stage.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === stage.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div
                    className="h-3 w-3 rounded-full mr-2 shrink-0"
                    style={{ backgroundColor: stage.color }}
                  />
                  <span>{stage.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
